<template>
    <div v-if="!isLoading"
        :class="['page',
                 {'page--add-to-home-screen-banner': isAddToHomeScreenBannerVisible,
                  'page--embed': partnerAppEmbedCustomization,
                  'page--with-buttons': buttons.length}]"
    >
        <section class="section section--has-action-buttons">
            <div class="section__container">
                <div class="section__title section__title--main">
                    <back-button class="back-button"
                        :isAbsolute="false"
                        :isBlack="true"
                        :isHiddenOnDesktop="false"
                    />
                    <h1>{{ partner.name }} <span class="light">{{ $t('wallet.wallet') }}</span></h1>
                </div>
                <div class="wallet-header">
                    <div class="token-card"
                        @click="goToPartner"
                    >
                        <partner-logo class="token-card__logo"
                            :show-placeholder="true"
                            :url="partner.partnerLogo"
                        />
                        <div class="token-card__details">
                            <div class="token-card__name">{{ token.currency.label }}</div>
                            <div class="token-card__amount"><span class="strong">{{ totalTokensAmount }} </span>{{ token.currency.shortCode }}</div>
                            <div v-if="isChzAllowed"
                                class="token-card__balance"
                            >
                                {{ chzAmount }} <span class="token-card__balance--alt">{{ alternativeBalance }}</span>
                            </div>
                            <div v-else
                                class="token-card__balance"
                            >
                                {{ fiatAmount }}
                            </div>
                        </div>
                    </div>
                    <div v-if="isVisibleForMarketplace"
                        class="token-table"
                    >
                        <div class="token-table__row">
                            <div class="token-table__cell">
                                <div class="token-table__label">{{ $t('wallet.partner.inUse') }}</div>
                                <div class="token-table__value">
                                    {{ Math.round(ledgerOverview.inUseBalance) }} {{ token.currency.shortCode }}
                                </div>
                            </div>
                            <div class="token-table__cell">
                                <div class="token-table__label">{{ $t('wallet.tradable') }}</div>
                                <div class="token-table__value">
                                    {{ Math.round(ledgerOverview.tradableBalance) }} {{ token.currency.shortCode }}
                                </div>
                            </div>
                        </div>
                        <div class="token-table__row">
                            <div class="token-table__cell token-table__cell-span">
                                <div class="token-table__label">{{ $t('wallet.partner.bonusOnHold') }}</div>
                                <div class="token-table__value">
                                    {{ Math.round(ledgerOverview.nonTradableBalance) }} {{ token.currency.shortCode }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="buttons.length"

                    :class="['action-buttons', {'action-buttons--embed': partnerAppEmbedCustomization}]"
                >
                    <router-link v-for="(item, index) in buttons"
                        :key="index"
                        :class="`wallet-button ${item.class}`"
                        :to="item.route"
                    >
                        {{ item.label }}
                    </router-link>
                </div>
            </div>
        </section>
        <section class="section">
            <div class="section__container">
                <div class="section__title">
                    <h2>{{ $t('wallet.partner.currentlyInUse') }}</h2>
                    <span class="strong">{{ Math.round(ledgerOverview.inUseBalance) }} {{ token.currency.shortCode }}*</span>
                </div>
                <wallet-details-card v-for="(detail, index) in inUseDetails"
                    :key="`in-use-${index}`"
                    :dateSinceLocked="detail.createdAt"
                    :dateToUnlock="detail.unlockAt"
                    :category="detail.category"
                    :icon="detail.icon"
                    :title="detail.title"
                    :amount="detail.amount"
                    :color="detail.color"
                    :currency="token.currency"
                />
                <template v-if="usageSuggestions.length">
                    <div class="section__subtitle">
                        <h3>{{ $t('wallet.suggestions').toUpperCase() }}</h3>
                        <p>{{ $t('wallet.suggestionsHint') }}</p>
                    </div>
                    <wallet-details-card v-for="(detail, index) in usageSuggestions"
                        :key="`suggestions-${index}`"
                        :icon="detail.icon"
                        :title="detail.title"
                        :subtitle="detail.subtitle"
                        :isSuggestion="true"
                        :onPress="detail.onPress"
                    />
                </template>
                <p class="section__footnote">{{ $t('wallet.partner.inUseFootnote') }}</p>
            </div>
        </section>
        <section v-if="nonTradableDetails.length"
            class="section"
        >
            <div class="section__container">
                <div class="section__title"><h2>{{ $t('wallet.partner.bonusOnHold') }}</h2></div>
                <wallet-details-card v-for="(detail, index) in nonTradableDetails"
                    :key="`on-hold-${index}`"
                    :category="detail.category"
                    :icon="detail.icon"
                    :title="detail.title"
                    :subtitle="detail.subtitle"
                    :amount="detail.amount"
                    :currency="token.currency"
                />
            </div>
        </section>
        <section class="section">
            <div class="section__container with-carousel">
                <div class="section__title">
                    <h2>{{ $t('nft.digitalCollectible') }}</h2>
                    <router-link class="row__header-link"
                        :to="{
                            name: 'wallet-digital-collectibles',
                        }"
                    >
                        {{ $t('global.seeAll') }}
                    </router-link>
                </div>
                <partner-wallet-nfts :partnerId="partner.id" />
            </div>
        </section>
        <section class="section">
            <div class="section__container">
                <div class="section__title">
                    <h2>{{ $t('global.transactionHistory') }}</h2>
                    <router-link class="row__header-link"
                        :to="{
                            name: 'transactions',
                        }"
                    >
                        {{ $t('global.seeAll') }}
                    </router-link>
                </div>
                <div v-if="transactions.length && !fetchingTransactions"
                    class="transactions"
                >
                    <transaction-card v-for="(item, index) in transactions"
                        :key="index"
                        :transaction="item"
                    />
                </div>
                <div v-if="!transactions.length && !fetchingTransactions"
                    class="transactions__empty"
                >
                    {{ $t('transactionHistory.noTransactionsMessage') }}
                </div>
            </div>
        </section>
    </div>
    <loading-spinner v-else
        class="spinner"
    />
</template>

<script>
// 3rd party libs
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Helpers
import {
    getPartnerPageBuySellButtons,
    getSuggestions,
} from '@webapp/helpers/partner.js';

// Mixins
import tokenCardMixin from '@webapp/helpers/token-card-mixin.js';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import partnerLogo from '@webapp/components/partner-logo/partner-logo.vue';
import walletDetailsCard from '@webapp/modules/crypto-and-trading/components/wallet/wallet-details-card.vue';
import transactionCard from '@webapp/modules/crypto-and-trading/views/wallet/transaction-card.vue';
import partnerWalletNfts from '@webapp/modules/crypto-and-trading/views/wallet/partner-wallet-nfts.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';

import {
    TransactionType,
} from '@shared/config/transactions-types';

export default {
    name: 'wallet-partner-details',
    components: {
        backButton,
        partnerLogo,
        walletDetailsCard,
        partnerWalletNfts,
        transactionCard,
        loadingSpinner,
    },
    mixins: [tokenCardMixin],
    beforeRouteEnter(to, from, next) {

        // Redirect to wallet after refreshing the page
        if (!from.name && from.path === '/') {
            next('/wallet');
        }

        next();
    },
    data() {
        return {
            buttons: [],
            fetchingLedgerOverview: true,
            fetchingAvailableMarkets: true,
            fetchingTeamInfoForPartnerId: true,
            fetchingTransactions: true,
            currentTransactionType: TransactionType.FanToken,
        };
    },
    computed: {
        ...mapGetters([
            'voteTokenOfferingsByStatusAndTokenId',
            'isFeatureDenied',
            'isAddToHomeScreenBannerVisible',
            'partnerAppEmbedCustomization',
        ]),

        ...mapGetters('wallet', [
            'ledgerOverview',
            'decentralizedLedgerByToken',
        ]),

        ...mapGetters('marketplace', [
            'availableMarkets',
            'marketByPartnerId',
        ]),

        ...mapGetters('voteTokens', [
            'isTokenPurchaseRestricted',
            'isTokenSaleRestricted',
            'getVoteTokenByShortCode',
        ]),

        ...mapGetters('predictorSportEvents', ['getTeamInfoForPartnerId']),

        ...mapGetters('transactions', ['formattedTransactionsList']),

        ...mapGetters('remoteConfig', ['remoteConfigByKey']),

        isLoading() {
            return this.fetchingLedgerOverview ||
                this.fetchingAvailableMarkets ||
                this.fetchingTeamInfoForPartnerId ||
                this.fetchingTransactions;
        },

        token() {
            return this.decentralizedLedgerByToken(this.partner.currencyId);
        },

        voteTokenOfferings() {
            return this.voteTokenOfferingsByStatusAndTokenId(['opened', 'published'], this.token.id);
        },

        isVisibleForMarketplace() {
            return this.getVoteTokenByShortCode(this.token.currency.shortCode).options?.visibleForMarketplace;
        },

        hasSellHidden() {
            return this.isTokenSaleRestricted(this.partnerMarket?.baseCurrency);
        },

        hasBuyHidden() {
            return this.isTokenPurchaseRestricted(this.partnerMarket?.baseCurrency);
        },

        partnerMarket() {
            return this.availableMarkets.find(({ partnerId }) => partnerId === this.partner.id);
        },

        inUseDetails() {
            return this.ledgerOverview?.inUseDetails ?? [];
        },

        teamInfo() {
            return this.getTeamInfoForPartnerId(this.partner.id);
        },

        usageSuggestions() {
            if (!this.teamInfo) {
                return [];
            }

            if (!this.inUseDetails) {
                return getSuggestions({
                    teamShortName: this.teamInfo.teamShortName,
                    partnerId: this.partner.id,
                    teamId: this.teamInfo.teamId,
                    vue: this,
                    currentRegulation: this.currentRegulation,
                });
            }

            return getSuggestions({
                teamShortName: this.teamInfo.teamShortName,
                partnerId: this.partner.id,
                teamId: this.teamInfo.teamId,
                vue: this,
                currentRegulation: this.currentRegulation,
            }).filter((suggestion) => !this.inUseDetails.find((detail) => detail.icon === suggestion.icon));
        },

        nonTradableDetails() {
            return this.ledgerOverview?.nonTradableDetails ?? [];
        },

        transactions() {
            return this.formattedTransactionsList.length ? this.formattedTransactionsList(this.currentTransactionType) : [];
        },
    },
    watch: {
        availableMarkets(newAvailableMarkets) {
            this.setBuySellBtns(newAvailableMarkets);
        },
    },
    mounted() {
        this.fetchLedgerOverview({
            ledgerId: this.token.id,
        }).then(() => {
            this.fetchingLedgerOverview = false;
        });

        this.fetchAvailableMarkets().then(() => {
            this.fetchingAvailableMarkets = false;
        });

        this.fetchTeamInfoForPartnerId({
            partnerId: this.partner.id,
        }).then(() => {
            this.fetchingTeamInfoForPartnerId = false;
        });

        this.fetchTransactions({
            type: this.currentTransactionType,
            limit: 1000,
            page: 1,
            filtersRequest: {
                currencyShortCodes: [this.token.currency.shortCode],
            },
        }).then(() => {
            this.fetchingTransactions = false;
        });

    },
    destroyed() {
        this.resetLedgerOverview();
    },
    methods: {
        ...mapActions('wallet', [
            'fetchLedgerOverview',
            'resetLedgerOverview',
        ]),

        ...mapActions('marketplace', ['fetchAvailableMarkets']),

        ...mapActions('predictorSportEvents', ['fetchTeamInfoForPartnerId']),

        ...mapActions('transactions', ['fetchTransactions']),

        setBuySellBtns(market) {
            this.buttons = getPartnerPageBuySellButtons({
                partner: this.partner,
                voteTokenOfferings: this.voteTokenOfferings,
                markets: market,
                hasSellHidden: this.hasSellHidden,
                hasBuyHidden: this.hasBuyHidden,
                $vue: this,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
h1,
h2,
h3,
p {
    margin: 0;
}

.bold {
    font-weight: 700;
}

.strong {
    font-weight: 700;
    font-size: 1.5rem;
}

.small {
    font-size: 1.1rem;
    color: $blue-comment-text;
}

.spinner {
    position: absolute;
    top: 50%;
    left: 50%;

    @include max-width(md) {
        transform: translate(-50%, -50%);
    }
}

.page {
    background: $athens-gray-color;
    color: $daintree-color;
    font-size: 1.3rem;

    @include max-width(md) {
        margin-bottom: 0;

        &--with-buttons {
            margin-bottom: 5rem;
        }
    }

    &--add-to-home-screen-banner {
        @include max-width(md) {
            margin-bottom: 9rem;
        }

        .section--has-action-buttons {
            .action-buttons {
                bottom: 10.5rem;
            }
        }
    }
}

.section {
    position: relative;
    padding: 2rem 0;

    &:before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(180deg, $white-color 0%, $athens-gray-color 70.48%);
    }

    &--has-action-buttons {

        @include max-width(md) {
            z-index: 10;
        }
    }

    &__container {
        max-width: 80.4rem;
        margin: 0 auto;
        position: relative;
        z-index: 1;

        & > * {
            margin-left: 1.7rem;
            margin-right: 1.7rem;
        }
    }

    &__title {
        position: relative;
        margin-bottom: 1.2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;

        &--main {
            justify-content: unset;
        }

        h1,
        h2 {
            font-size: 2.1rem;
            font-weight: 700;
        }

        .light {
            font-size: 1.7rem;
            font-weight: 400;
            color: $blue-comment-text;
        }
    }

    &__subtitle {
        margin-bottom: 2rem;
        margin-top: 2rem;

        h3 {
            margin-bottom: 0.2rem;
            font-size: 1.3rem;
            font-weight: 700;
        }
    }

    &__footnote {
        position: relative;
        top: 0.6rem;
        font-size: 1.3rem;
    }
}

.with-carousel {
    max-width: calc(80.4rem + 4rem);

    .section__title {
        padding: 0 2rem;
    }

    @include max-width(1150px) {
        max-width: none;
        width: calc(100% - 100px);
    }

    @include max-width(md) {
        width: 100%;

        .section__title {
            padding: 0;
        }
    }
}

.back-button {
    margin-right: 1.5rem;
    left: 0;

    @include max-width(xl) {
        left: 0;
        top: 0;
        position: static;
        margin-right: 2rem;
    }

    margin-left: 0;
}

.wallet-header {
    display: flex;
    justify-content: space-between;

    @include max-width(lg) {
        flex-direction: column;
    }

    & > * {
        max-width: calc(50% - 0.4rem);

        @include max-width(lg) {
            max-width: 100%;
        }

        &:first-child {
            margin-right: 0.4rem;

            @include max-width(lg) {
                margin-right: 0;
            }
        }

        &:nth-child(2) {
            margin-left: 0.4rem;

            @include max-width(lg) {
                margin-left: 0;
            }
        }
    }

    & > .token-card {
        max-width: none;
    }
}

.token-card {
    display: flex;
    flex: 1;
    background: $white-color;
    border-radius: 8px;
    padding: 1.3rem 2rem;
    cursor: pointer;
    box-sizing: border-box;

    &__logo {
        height: 5.5rem;
        width: 5.5rem;
        margin-right: 2rem;
    }

    &__details {
        display: flex;
        flex: 1;
        flex-wrap: wrap;
        justify-content: space-between;
    }

    &__name {
        max-width: 70%;
        flex: 1;
        font-weight: 700;
        font-size: 1.3rem;
        padding-bottom: 0.4rem;
        align-self: flex-end;
    }

    &__amount {
        max-width: 30%;
        text-align: right;
        font-size: 1.5rem;
        padding-bottom: 0.4rem;
        align-self: flex-end;
    }

    &__balance {
        width: 100%;
        padding-top: 0.4rem;
        border-top: 0.1rem solid $blue-dark-color;
        font-size: 1.1rem;

        &--alt {
            color: $hit-gray-color;
        }
    }
}

.token-table {
    display: flex;
    flex-direction: column;
    flex: 1;

    @include max-width(lg) {
        margin-top: 2rem;
    }

    &__row {
        display: flex;
        flex: 1;
        flex-wrap: nowrap;
        justify-content: space-between;

        &:first-child {
            margin-bottom: 0.4rem;
        }
    }

    &__cell {
        max-width: calc(50% - 0.4rem);
        padding: 0.7rem 2.4rem;
        flex: 1;
        background: $background-gray-color;
        border-radius: 8px;
        box-sizing: border-box;
        line-height: 1.2;

        &:first-child {
            margin-right: 0.4rem;
        }

        &:nth-child(2) {
            margin-left: 0.4rem;
        }

        &-span {
            max-width: 100%;
            display: flex;
            align-items: center;

            &:first-child {
                margin: 0;
            }

            .token-table__label {
                flex: 1;
            }

            .token-table__value {
                flex: 1;
                padding-left: 6rem;
            }
        }
    }

    &__label {
        font-size: 1.3rem;
    }

    &__value {
        font-size: 1.5rem;
        font-weight: 700;
    }
}

.action-buttons {
    margin-top: 5rem;
    display: flex;
    box-sizing: border-box;

    @include max-width(lg) {
        margin-top: 4rem;
    }

    & > * {
        max-width: calc(50% - 0.4rem);
    }
}

.wallet-button {
    display: block;
    max-height: 4rem;
    padding: 1.2rem 4.5rem;
    font-size: 1.5rem;
    line-height: 1;
    text-decoration: none;
    text-align: center;
    color: $white-color;
    border-radius: 4px;
    border: 0;
    flex: 1;

    &:first-child {
        margin-right: 0.4rem;
    }

    &:nth-child(2) {
        margin-left: 0.4rem;
    }

    &.button--orange {
        background: $orange-peel-color;
    }

    &.button--blue {
        background: $blue-ribbon-color;
    }

    &--green {
        background: $lime-green-color;
        color: $black-color;
    }
}

.action-button {
    flex: 1;
    width: 100%;
    align-self: flex-end;
    margin-top: 2rem;

    .wallet-button {
        margin-right: 0;
    }
}

.action-button,
.action-buttons {

    @include max-width(md) {
        background: $porcelain-color;
        position: fixed;
        left: 0;
        z-index: 10;
        bottom: 6.5rem;
        width: 100%;
        margin: 0;
        border-top: 0.2rem solid $mercury-color;
        padding: 0.5rem;
        box-sizing: border-box;

        &--embed {
            bottom: 0;
        }
    }
}

.transactions {
    background: #fff;
    border-radius: 8px;
    padding: 0 2rem;

    &__empty {
        font-size: 1.4rem;
        text-align: center;
        margin-top: 5rem;
    }
}
</style>